/**
 * ProseMirror Plugin Manager for Kendo Editor
 * Manages and configures ProseMirror plugins that integrate with Kendo Editor
 * Uses Kendo Editor's internal ProseMirror instances to avoid version conflicts
 */
import {
  type AutolinkPluginOptions,
  TRIGGER_CHARS,
  URL_REGEX,
  createAutolinkPlugin
} from "./auto-link-plugin";
import {
  DEFAULT_STYLE_TYPES,
  type OrderedListStylePluginOptions,
  createOrderedListStylePlugin
} from "./ordered-list-style-plugin";
import { handleEditorError } from "../hooks";
import * as React from "react";

// ============================================================================
// PLUGIN MANAGER CONFIGURATION - Now uses centralized constants
// ============================================================================

export interface PluginManagerOptions {
  autolink?: {
    enabled: boolean;
    options?: AutolinkPluginOptions;
  };
  orderedListStyle?: {
    enabled: boolean;
    options?: OrderedListStylePluginOptions;
  };
}

// Simplified configuration using centralized constants - no more duplication
const DEFAULT_PLUGIN_OPTIONS: Required<PluginManagerOptions> = {
  autolink: {
    enabled: true,
    options: {
      triggerChars: TRIGGER_CHARS,
      urlRegex: URL_REGEX
    }
  },
  orderedListStyle: {
    enabled: true,
    options: {
      styleTypes: DEFAULT_STYLE_TYPES
    }
  }
};

// ============================================================================
// PLUGIN MANAGER CLASS
// ============================================================================

export class KendoEditorPluginManager {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private plugins: any[] = [];
  private options: Required<PluginManagerOptions>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private view: any = null;

  constructor(options: PluginManagerOptions = {}) {
    this.options = { ...DEFAULT_PLUGIN_OPTIONS, ...options };
    this.initializePlugins();
  }

  private initializePlugins(): void {
    try {
      this.plugins = [];

      if (this.options.autolink.enabled) {
        const autolinkPlugin = createAutolinkPlugin(
          this.options.autolink.options
        );
        this.plugins.push(autolinkPlugin);
      }

      if (this.options.orderedListStyle.enabled) {
        const orderedListStylePlugin = createOrderedListStylePlugin(
          this.options.orderedListStyle.options
        );
        this.plugins.push(orderedListStylePlugin);
      }
    } catch (error) {
      handleEditorError(error as Error, "plugin manager initialization");
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getPlugins(): any[] {
    return [...this.plugins];
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setEditorView(view: any): void {
    this.view = view;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getEditorView(): any {
    return this.view;
  }

  destroy(): void {
    try {
      this.plugins = [];
      this.view = null;
    } catch (error) {
      handleEditorError(error as Error, "plugin manager cleanup");
    }
  }
}

// ============================================================================
// PLUGIN INTEGRATION HELPERS
// ============================================================================

export const createKendoEditorPluginManager = (
  options: PluginManagerOptions = {}
): KendoEditorPluginManager => {
  return new KendoEditorPluginManager({
    autolink: {
      enabled: true,
      options: {
        triggerChars: TRIGGER_CHARS,
        urlRegex: URL_REGEX
      }
    },
    orderedListStyle: {
      enabled: true,
      options: {
        styleTypes: DEFAULT_STYLE_TYPES
      }
    },
    ...options
  });
};

let globalPluginManagerInstance: KendoEditorPluginManager | null = null;

const getGlobalPluginManager = (
  options?: PluginManagerOptions
): KendoEditorPluginManager => {
  if (!globalPluginManagerInstance) {
    globalPluginManagerInstance = createKendoEditorPluginManager(options);
  }

  return globalPluginManagerInstance;
};

export const usePluginManager = (options?: PluginManagerOptions) => {
  const manager = getGlobalPluginManager(options);

  React.useEffect(() => {
    return () => {
      // Cleanup handled by global singleton
    };
  }, []);

  return manager;
};
